/* eslint-disable react/jsx-no-bind */
import { ErrorBoundary } from "~/lib/Bugsnag";
import { ErrorView } from "~/components/shared/ErrorView";
import { useNavigate } from "react-router-dom";
import { TableView } from "~/components/build/Show/components/TableView";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useFilterStore } from "~/components/build/Show/lib/useFilterStore";
import { noop } from "lodash";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useAllJobsQuery } from "~/components/build/Show/lib/queries/allJobs";
import { jobsQueryOptions } from "~/components/build/Show/lib/queries/jobs";
import { useMemo } from "react";

export const Table = ({ className }) => {
  const { build } = useBuild();
  const filters = useFilterStore((state) => state.filterBy);
  const tableSearchValue = useBuildPageStore((state) => state.tableSearchValue);
  const showRetriedJobs = useBuildPreferencesStore((state) => state.showRetriedJobs);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage } =
    useInfiniteQuery(
      jobsQueryOptions(build, filters, showRetriedJobs, tableSearchValue, {
        placeholderData: keepPreviousData,
        enabled: Features.BuildTablePaginatedJobs,
      }),
    );

  const allJobsQuery = useAllJobsQuery(build, showRetriedJobs);

  const paginatedJobs = useMemo(() => {
    return data?.pages.flatMap((page) => page.records) ?? [];
  }, [data]);

  if (Features.BuildTablePaginatedJobs) {
    return (
      <TableView
        jobs={paginatedJobs}
        filters={filters}
        isLoading={isLoading}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        className={className}
      />
    );
  }

  return (
    <TableView
      jobs={allJobsQuery.data?.records || []}
      isLoading={allJobsQuery.isLoading}
      filters={filters}
      hasNextPage={false}
      isFetching={false}
      isFetchingNextPage={false}
      fetchNextPage={noop}
      className={className}
    />
  );
};

export default function TablePage() {
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      FallbackComponent={() => <ErrorView className="px-4 py-2" clearError={() => navigate(0)} />}
    >
      <div
        className="relative min-w-0 flex-auto rounded-b-md bg-white contain-layout"
        data-testid="table"
      >
        <Table className="absolute" />
      </div>
    </ErrorBoundary>
  );
}
